/**
 * Module #28 chunk 3 — auth redirect URL helpers.
 *
 * Returns the right base origin for Supabase auth links depending on
 * runtime:
 *
 *   - In a Capacitor app  → "auctisync://"   (custom URL scheme; iOS +
 *                                              Android route the link back
 *                                              to the installed app)
 *   - In a browser        → window.location.origin   (existing behavior)
 *
 * Used by SignInForm / ForgotPassword to set `emailRedirectTo` +
 * `redirectTo` on Supabase auth calls. The corresponding handler is
 * `useCapacitorDeepLinkAuth` (in-app URL handoff) + the React
 * `AuthCallback` route (session-finalization + smart routing).
 *
 * IMPORTANT: ANY value returned here that's not an exact match against
 * Supabase's allowlist will be rejected with a redirect mismatch error.
 * The allowlist must include both:
 *   - https://app.auctisync.com/auth/callback   (web, existing)
 *   - auctisync://auth/callback                 (native, new in chunk 3)
 * Added to Supabase Dashboard → Authentication → URL Configuration as a
 * Jim-hands prereq.
 */

const NATIVE_SCHEME = 'auctisync://';

interface CapacitorCoreShape {
  isNativePlatform: () => boolean;
}

/**
 * Synchronous best-effort detection — reads the already-loaded
 * `@capacitor/core` global if present on window. Stays sync so callers
 * (form submit handlers) don't need to await.
 *
 * Capacitor sets `globalThis.Capacitor` at native-shell init, before any
 * React code runs. In a regular browser this global doesn't exist, so the
 * function returns false instantly.
 */
function detectNativePlatform(): boolean {
  if (typeof globalThis === 'undefined') return false;
  const Capacitor = (globalThis as { Capacitor?: CapacitorCoreShape }).Capacitor;
  if (!Capacitor || typeof Capacitor.isNativePlatform !== 'function') return false;
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Build a Supabase auth redirect URL pointing at the right surface.
 *
 *   authRedirectUrl('/auth/callback')                       →
 *     web    : "https://app.auctisync.com/auth/callback"
 *     native : "auctisync://auth/callback"
 *
 *   authRedirectUrl('/auth/callback?next=/admin/dashboard') →
 *     web    : "https://app.auctisync.com/auth/callback?next=..."
 *     native : "auctisync://auth/callback?next=..."
 */
export function authRedirectUrl(path: string): string {
  // Normalize leading slash so concat math works for both web origin +
  // custom scheme bases.
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  if (detectNativePlatform()) {
    return `${NATIVE_SCHEME}${normalized}`;
  }
  // SSR-safe — fall back to a hardcoded production origin so the function
  // is callable from a server-side environment in the future (today this
  // path doesn't execute because all callers run client-side).
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://app.auctisync.com';
  return `${origin}/${normalized}`;
}

/**
 * Test-only escape hatch — re-evaluates the native flag instead of
 * relying on a module-loaded cache. We have no cache today, so this is a
 * passthrough. Reserved as a stable API so adding caching later doesn't
 * break tests.
 */
export function _detectNativeForTests(): boolean {
  return detectNativePlatform();
}
